import { requireService, ScopeDIProvider } from '@profgeosoft/di-ez';
import { ExpandDownLight, ExpandUpLight } from '@profgeosoft-ui/icons';
import { Drawer } from '@profgeosoft-ui/react';
import { Table, useGridApiRef } from '@profgeosoft-ui/table';
import clsx from 'clsx';
import { reaction } from 'mobx';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';
import { debounce } from 'src/packages/utils/debounce';
import { hasValue } from 'src/packages/utils/has-value';
import { InterTablesInteractions } from 'src/services/inter-tables-interactions';

import type { GridColumnResizeParams, GridSortItem, GridCellParams } from '@profgeosoft-ui/table';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { MainDirectoryService } from 'src/services/directory-service/main-directory-service';
import type { TTableSortItem } from 'src/services/table-settings-service/table-settings-service';

import { DisplaySettings } from '../display-settings/display-settings';
import { ErrorBoundary } from '../error-boundary/error-boundary';

import { SERVICE_COLUMNS } from './consts';
import { DirectoryTableMediator } from './directory-table-mediator';
import { useGetColumnsGrouping } from './hooks/use-get-columns-grouping';
import { useDetailPanel } from './hooks/use-get-detail-panel-content';
import { useTableColumns } from './hooks/use-table-columns';
import { useTableEventHandlers } from './hooks/use-table-event-handlers';
import { tableSx } from './table-sx';

import styles from './directory-table.module.scss';

type Props = {
  directory: MainDirectoryService;
  isRowSelectionModeOn?: boolean;
};

const muiLanguageKeys: Record<string, 'ruRU' | 'enUS' | 'esES'> = {
  ru: 'ruRU',
  en: 'enUS',
  es: 'esES',
};

const MIN_VISIBLE_COLUMNS = 3;

export const DirectoryTable = wrap<Props>(function DirectoryTable({ directory, isRowSelectionModeOn }) {
  const [renderKey, setRenderKey] = useState(1);
  const displayManager = directory.tableSettingsManager;
  const [interTableInteractions] = useState(() => new InterTablesInteractions());
  const notifications = requireService('notificationsService');
  const localization = requireService('localizationService');
  const directoryPage = requireService('directoryPage');
  const tableApi = useGridApiRef();
  const columns = useTableColumns(directory);
  const [tableMediator] = useState(() => new DirectoryTableMediator(directory, tableApi, notifications, localization));
  const groupingModel = useGetColumnsGrouping(directory.view);
  const { handleCellDeletePress, handleComboboxEnterPress } = useTableEventHandlers({
    tableApi,
    store: tableMediator,
    directory,
    interTableInteractions,
  });

  const { t } = useTranslation('directory');

  const { getDetailPanelContent, getPanelHeight } = useDetailPanel(directory);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(({ colDef: { field }, width }: GridColumnResizeParams) => {
      if (!displayManager) {
        return;
      }

      const updatedColumnsWidths: Record<string, number> = {
        ...(displayManager.settings.columnsWidth ?? {}),
        [field]: width,
      };

      displayManager.onWidthChange(updatedColumnsWidths);
    }, 300),
    [displayManager],
  );

  const handleTableSettingsClick = useCallback(() => {
    displayManager?.setSettingsOpen(true);
    tableApi.current.hideColumnMenu();
  }, [displayManager, tableApi]);

  const slotProps = useMemo(
    () => ({
      columnMenu: {
        onTableSettingsClick: handleTableSettingsClick,
        minVisibleColumns: MIN_VISIBLE_COLUMNS,
      },
    }),
    [handleTableSettingsClick],
  );

  const convertToTableSortItems = useCallback((model: GridSortItem[]): TTableSortItem[] => {
    const convertedSortItems: TTableSortItem[] = [];

    model.forEach((item) => {
      if (item.sort === 'asc' || item.sort === 'desc') {
        convertedSortItems.push({
          field: item.field,
          sort: item.sort,
        });
      }
    });

    return convertedSortItems;
  }, []);

  const onSortModelChange = useCallback(
    (model: GridSortItem[]) => {
      displayManager.onSortChange(convertToTableSortItems(model));
    },
    [convertToTableSortItems, displayManager],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<DirectoryRecord>): string => {
      const record = directory.recordsMap.get(params.id);

      if (!record) {
        return '';
      }

      const { fieldRulesData, fieldsErrors } = record;

      const isSelected =
        displayManager?.selectedCellData &&
        displayManager?.selectedCellData.cellField === params.field &&
        displayManager?.selectedCellData.rowId === params.row.recordId;

      return clsx(
        styles.cell,
        fieldsErrors[params.field]?.length && 'go-table__error-cell',
        isSelected && `${styles.selectedCell} Mui-selected`,
        directoryPage.isEditing &&
          (fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked) &&
          styles.blockedCell,
      );
    },
    [directory, directoryPage.isEditing, displayManager?.selectedCellData],
  );

  const slots = useMemo(
    () => ({
      detailPanelExpandIcon: ExpandDownLight,
      detailPanelCollapseIcon: ExpandUpLight,
    }),
    [],
  );

  useEffect(() => {
    if (!displayManager) {
      return;
    }

    if (!displayManager.settings?.columnsWidth) {
      tableApi.current.autosizeColumns();

      return;
    }

    for (const column of tableApi.current.getAllColumns()) {
      if (
        displayManager.settings.columnsWidth[column.field] === column.width ||
        !hasValue(displayManager.settings.columnsWidth[column.field])
      ) {
        continue;
      }

      tableApi.current.setColumnWidth(column.field, displayManager.settings.columnsWidth[column.field]);
    }
  }, [tableApi, columns, displayManager, displayManager?.settings]);

  useEffect(() => {
    if (!displayManager) {
      return;
    }

    if (!displayManager.settings.columnsOrderModel) {
      displayManager.onColumnsOrderModelChange(columns.map((columns) => columns.field));
    }
  }, [columns, displayManager, tableApi]);

  useEffect(() => {
    directory.setDirectoryViewMediator(tableMediator);

    directoryPage.pluginsService.plugins.forEach((plugin) => {
      plugin.pluginController.setTableMediator(tableMediator);
    });

    return tableMediator.init();
  }, [directory, localization, notifications, tableApi, directoryPage.pluginsService.plugins, tableMediator]);

  useEffect(() => {
    return reaction(
      () => interTableInteractions.activeTable,
      (activeTable) => {
        if (directory.entityId === activeTable) {
          return;
        }

        tableApi.current.setCellSelectionModel({});
        displayManager.setSelectedCellData(null);
      },
    );
  }, [directory, displayManager, interTableInteractions, tableApi]);

  useEffect(() => {
    return tableApi.current.subscribeEvent('columnSeparatorDoubleClick', ({ field }) => {
      const col = tableApi.current.getColumn(field);
      const width = col.width;

      if (hasValue(width)) {
        handleWidthChange({ colDef: col, width });
      }
    });
  }, [handleWidthChange, tableApi]);

  const init = useCallback(
    () => ({
      interTableInteractions,
    }),
    [interTableInteractions],
  );

  if (!displayManager) {
    return null;
  }

  return (
    <Fragment key={renderKey}>
      <ErrorBoundary
        errorStub={<div>Table error</div>}
        onError={(error) => {
          tableMediator.onError(error);

          setRenderKey((key) => {
            return key + 1;
          });
        }}
      >
        <ScopeDIProvider init={init}>
          <div className={styles.tableWrapper}>
            <Table
              initialState={{
                filter: {
                  filterModel: displayManager.settings.filterModel,
                },
                sorting: {
                  sortModel: displayManager.settings.sortingModel,
                },
                pinnedColumns: displayManager.settings.pinnedColumns,
              }}
              apiRef={tableApi}
              columns={columns}
              rows={directory.records}
              sx={tableSx}
              language={muiLanguageKeys[localization.language]}
              valueDragging
              onValueDraggingEnd={directory.directoryViewMediator?.handleValueDraggingEnd}
              serviceColumns={SERVICE_COLUMNS}
              slots={slots}
              columnsOrderModel={displayManager.columnsOrderModel}
              columnVisibilityModel={displayManager.settings.columnsVisibility ?? {}}
              pinnedColumns={displayManager.settings.pinnedColumns}
              onCellEditStop={handleComboboxEnterPress}
              onCellEditStart={handleCellDeletePress}
              onFilterModelChange={displayManager.onFilterChange}
              onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
              onPinnedColumnsChange={displayManager.onPinChange}
              onSortModelChange={onSortModelChange}
              onColumnResize={handleWidthChange}
              getDetailPanelContent={getDetailPanelContent ?? undefined}
              getDetailPanelHeight={getPanelHeight}
              isCellEditable={(params) => {
                const row = directory.recordsMap.get(params.id as number);
                const fieldRulesData = row?.fieldRulesData[params.field];

                if (!fieldRulesData) {
                  return !!params.colDef.editable;
                }

                return !(fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked);
              }}
              onColumnsOrderModelChange={displayManager.onColumnsOrderModelChange}
              slotProps={slotProps}
              className={styles.table}
              checkboxSelection={isRowSelectionModeOn}
              disableAggregation
              disableRowGrouping
              cellSelection
              loading={directory.isDataFetching || directory.isAdditionalDirectoriesFetching}
              columnGroupingModel={groupingModel}
              getRowId={(row) => row.recordId}
              onRowSelectionModelChange={(model) => {
                directory.directoryViewMediator?.setIsCopyAvailable(!!model.length);
                directory.directoryViewMediator?.updateSelectedRows(model);
              }}
              onClipboardCopy={(_, event) => {
                event.defaultMuiPrevented = true;
              }}
              onCellKeyDown={(_, event) => {
                if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
                  event.defaultMuiPrevented = true;
                }
              }}
              getCellClassName={getCellClassName}
              getRowClassName={({ id }) => {
                const classNames: string[] = [];

                if (typeof id === 'number') {
                  const isActive = directory.recordsActivity[id];

                  if (isActive) {
                    classNames.push(clsx(styles.disactiveRow));
                  }
                }

                return '';
              }}
            />
          </div>
        </ScopeDIProvider>
      </ErrorBoundary>
      <Drawer
        className={styles.displaySettingsWrapper}
        style={{ width: 320 }}
        open={displayManager.isSettingsOpen}
        onClose={() => displayManager.setSettingsOpen(false)}
        title={<p className={styles.title}>{t('displaySettings:title')}</p>}
      >
        <DisplaySettings
          fieldViews={directory.recordControlsViews}
          columns={columns}
          displayManager={displayManager}
          minVisibleColumns={MIN_VISIBLE_COLUMNS}
        />
      </Drawer>
    </Fragment>
  );
});

declare module '@profgeosoft/di-ez' {
  export interface ServicesCollectionMap {
    interTableInteractions: InterTablesInteractions;
  }
}
